import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { canAccess, hasAdminAccess } from '../../rbac';
import { tenantAPI } from '../../services/api';
import { canOpenRegistryReviewQueue } from '../../pages/admin/registryReviewQueueUi';
import MembershipSwitcher from '../MembershipSwitcher';
import GoogleTranslate from '../common/GoogleTranslate';
import { safeLocalStorageSet } from '../../utils/storage';
import './AdminLayout.css';

const DEFAULT_GP_LOGO = '/certificate-assets/ashoka-emblem.png';

export default function AdminLayout() {
  const { user, tenant, logout, setTenant } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const tenantSlug = tenant?.slug;

  useEffect(() => {
    if (user && !hasAdminAccess(user.role, user)) {
      navigate(`/gp/${tenantSlug || ''}`);
    }
  }, [user, tenantSlug, navigate]);

  useEffect(() => {
    if (!tenantSlug || tenant?.logo_url) return;
    tenantAPI.getBySlug(tenantSlug).then(response => {
      const freshTenant = response.data.tenant || response.data;
      if (!freshTenant?.logo_url) return;
      const nextTenant = { ...tenant, logo_url: freshTenant.logo_url };
      setTenant(nextTenant);
      safeLocalStorageSet('tenant', nextTenant);
    }).catch(() => { });
  }, [tenant, tenantSlug, setTenant]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const sidebarItems = [
    { path: 'dashboard', key: 'dashboard', label: 'डॅशबोर्ड', icon: '📊' },
    { path: 'households', key: 'households', label: 'कुटुंबे', icon: '🏠' },
    { path: 'citizens', key: 'citizens', label: 'नागरिक', icon: '👥' },
    { path: 'certificates', key: 'certificates', label: 'प्रमाणपत्रे', icon: '📄' },
    { path: 'services-certificates', key: 'services', keys: ['services', 'certificates', 'content'], label: 'सेवा / प्रमाणपत्र सेटिंग', icon: '🧾' },
    { path: 'complaints', key: 'complaints', label: 'तक्रारी', icon: '📢' },
    { path: 'schemes', key: 'schemes', label: 'योजना', icon: '🎯' },
    { path: 'gramsabha', key: 'gramsabha', label: 'ग्रामसभा', icon: '🏛️' },
    { path: 'finance', key: 'finance', label: 'वित्त', icon: '💰' },
    { path: 'tax-workspace', key: 'tax_assessments', keys: ['tax_assessments', 'tax_configuration', 'tax_collection', 'tax_reports'], label: 'कर व्यवस्थापन / घरपट्टी व व्यवसाय', icon: '🧾' },
    { path: 'registry-change-requests', key: 'registry_change_requests', canShow: canOpenRegistryReviewQueue, label: 'नोंद बदल पडताळणी', icon: '✓' },
    { path: 'properties', key: 'properties', label: 'नवीन मालमत्ता नोंदणी', icon: '⌂' },
    { path: 'projects', key: 'projects', label: 'प्रकल्प', icon: '🏗️' },
    { path: 'content', key: 'content', label: 'वेबसाइट सामग्री', icon: '🌐' },
    { path: 'notices', key: 'notices', label: 'सूचना', icon: '📋' },
    { path: 'contact-messages', key: 'content', keys: ['content', 'dashboard'], label: 'नागरिक संदेश', icon: '✉️' },
    { path: 'facilities', key: 'facilities', label: 'सुविधा', icon: '🏥' },
    { path: 'businesses', key: 'businesses', label: 'व्यवसाय', icon: '🏪' },
    { path: 'business-compliance', key: 'business_compliance', label: 'व्यवसाय अनुपालन', icon: '🛡️' },
    { path: 'departments', key: 'departments', label: 'विभाग', icon: '🏢' },
    { path: 'staff', key: 'staff', label: 'कर्मचारी', icon: '👔' },
    { path: 'user-approvals', key: 'user_approvals', label: 'वापरकर्ता मंजुरी', icon: '🛡️' },
    { path: 'audit-log', key: 'audit_log', label: 'Audit Log', icon: '☷' },
    { path: 'documentation', key: '_always', label: 'मदत / मार्गदर्शन', icon: '📖' },
  ];

  const visibleItems = sidebarItems.filter(item => (
    item.key === '_always'
    || (item.canShow ? item.canShow(user) : (item.keys || [item.key]).some(key => canAccess(user?.role, key, user)))
  ));

  return (
    <div className="admin-layout">
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <img
            className="sidebar-gp-logo"
            src={tenant?.logo_url || DEFAULT_GP_LOGO}
            alt={`${tenant?.name || 'ग्रामपंचायत'} लोगो`}
            onError={event => {
              if (event.currentTarget.src.endsWith(DEFAULT_GP_LOGO)) return;
              event.currentTarget.src = DEFAULT_GP_LOGO;
            }}
          />
          <div className="sidebar-brand-copy">
            <h2>ग्रा.पं. प्रशासन</h2>
            <p className="tenant-name">{tenant?.name || 'ग्रामपंचायत'}</p>
          </div>
        </div>
        <nav className="sidebar-nav">
          {visibleItems.map((item) => (
            <NavLink
              key={item.path}
              to={`/admin/${tenantSlug}/${item.path}`}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sidebar-icon">{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-footer">
          <NavLink to={`/gp/${tenantSlug}`} className={({ isActive }) => `sidebar-link view-site ${isActive ? 'active' : ''}`}>
            🔗 सार्वजनिक साइट पहा
          </NavLink>
        </div>
      </aside>

      <div className="admin-content">
        <header className="admin-header">
          <div className="admin-header-left">
            <button className="hamburger-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
              ☰
            </button>
            <h3>डिजिटल ग्रामपंचायत पोर्टल</h3>
          </div>
          <div className="admin-header-right">
            <GoogleTranslate variant="light" />
            <MembershipSwitcher compact />
            <span className="user-info">
              {user?.full_name} ({user?.role?.replace(/_/g, ' ')})
            </span>
            <Link to="profile" className="btn btn-outline btn-sm" style={{ textDecoration: 'none' }}>
              👤 प्रोफाइल
            </Link>
            <button onClick={handleLogout} className="btn btn-outline btn-sm">
              बाहेर पडा
            </button>
          </div>
        </header>

        <main className="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
